import type { Alert } from "../../types";
import { relTime } from "../../utils/format";

interface Props {
  alerts: Alert[];
  open: boolean;
  onClose: () => void;
  onSelect: (a: Alert) => void;
}

export function AlertDrawer({ alerts, open, onClose, onSelect }: Props) {
  if (!open) return null;
  const grouped = {
    critical: alerts.filter((a) => a.severity === "critical"),
    warning: alerts.filter((a) => a.severity === "warning"),
    info: alerts.filter((a) => a.severity === "info"),
  };
  return (
    <>
      <div
        className="fade-in"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(4,7,12,0.5)",
          backdropFilter: "blur(2px)",
          zIndex: 100,
        }}
      />
      <div
        className="drawer"
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 420,
          background: "var(--bg-1)",
          borderLeft: "1px solid var(--line-2)",
          zIndex: 101,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid var(--line)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>Active alerts</span>
          <span className="chip">{alerts.length}</span>
          <span style={{ flex: 1 }} />
          <button className="btn xs ghost" onClick={onClose}>
            esc ✕
          </button>
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {(["critical", "warning", "info"] as const).map(
            (sev) =>
              grouped[sev].length > 0 && (
                <div key={sev}>
                  <div
                    style={{
                      padding: "8px 16px",
                      fontSize: 10,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--fg-3)",
                      background: "var(--bg-2)",
                      borderBottom: "1px solid var(--line)",
                    }}
                  >
                    {sev} · {grouped[sev].length}
                  </div>
                  {grouped[sev].map((a) => (
                    <button
                      key={a.fingerprint}
                      onClick={() => onSelect(a)}
                      style={{
                        width: "100%",
                        padding: "10px 16px",
                        borderBottom: "1px solid var(--line)",
                        textAlign: "left",
                        display: "block",
                      }}
                      className="btn ghost"
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        <span
                          className={`chip ${
                            sev === "critical"
                              ? "crit"
                              : sev === "warning"
                              ? "warn"
                              : "info"
                          }`}
                        >
                          <span className="dot" />
                          {a.name}
                        </span>
                        <span
                          className="u-xdim mono"
                          style={{ fontSize: 10 }}
                        >
                          {relTime(a.fired_at)}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--fg-0)",
                          marginBottom: 3,
                        }}
                      >
                        {a.summary}
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: "var(--fg-2)",
                          fontFamily: "var(--mono)",
                        }}
                      >
                        team={a.team} ·{" "}
                        {Object.entries(a.labels)
                          .map(([k, v]) => `${k}="${v}"`)
                          .join(" ")}
                      </div>
                    </button>
                  ))}
                </div>
              )
          )}
          {!alerts.length && (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: "var(--fg-2)",
                fontSize: 12,
              }}
            >
              No active alerts — fleet is healthy.
            </div>
          )}
        </div>
      </div>
    </>
  );
}
